"use client";

import { ReactNode } from "react";
import { Button } from "./ui";

export type SortDir = "asc" | "desc";

// Compare helper for client-side sorting (numbers, strings, null-safe).
export function compareValues(a: unknown, b: unknown, dir: SortDir): number {
  const av = a ?? "";
  const bv = b ?? "";
  let r: number;
  if (typeof av === "number" && typeof bv === "number") r = av - bv;
  else r = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
  return dir === "asc" ? r : -r;
}

// A clickable table header that shows the active sort direction.
export function SortableTh({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  className = "",
  align = "left"
}: {
  label: string;
  sortKey: string;
  activeKey: string | null;
  dir: SortDir;
  onSort: (key: string) => void;
  className?: string;
  align?: "left" | "right";
}) {
  const active = activeKey === sortKey;
  return (
    <th className={`py-2 pr-4 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""} ${
          active ? "text-gray-700" : "hover:text-gray-700"
        }`}
      >
        {label}
        <span className={`transition ${active ? "opacity-100" : "opacity-0 group-hover:opacity-50"}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="h-3 w-3">
            {active && dir === "asc" ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            )}
          </svg>
        </span>
      </button>
    </th>
  );
}

// Controlled pagination footer with a range summary.
export function Pagination({
  offset,
  count,
  hasMore,
  loading,
  onPrev,
  onNext,
  total
}: {
  offset: number;
  count: number;
  hasMore: boolean;
  loading?: boolean;
  onPrev: () => void;
  onNext: () => void;
  total?: number;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-gray-500">
      <span>
        {count === 0 ? "No results" : (
          <>
            Showing <span className="font-medium text-gray-700">{offset + 1}</span>–
            <span className="font-medium text-gray-700">{offset + count}</span>
            {total !== undefined ? <> of <span className="font-medium text-gray-700">{total.toLocaleString()}</span></> : null}
          </>
        )}
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={offset === 0 || loading} onClick={onPrev}>
          Previous
        </Button>
        <Button variant="secondary" size="sm" disabled={!hasMore || loading} onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}

// A search input with a leading magnifier icon for table toolbars.
export function SearchInput({
  value,
  onChange,
  placeholder = "Search…",
  className = ""
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm transition placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
      />
    </div>
  );
}

// Standard status badge mapping used across report tables.
export function statusTone(status: string): "green" | "red" | "blue" | "amber" | "gray" {
  const s = (status || "").toUpperCase();
  if (s === "DELIVERED") return "green";
  if (["FAILED", "EXPIRED", "UNDELIVERABLE", "REJECTED"].includes(s)) return "red";
  if (["SUBMITTED", "ACCEPTED", "PROCESSING"].includes(s)) return "blue";
  if (["PENDING", "RETRY", "SCHEDULED"].includes(s)) return "amber";
  return "gray";
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-end justify-between gap-3">{children}</div>;
}
